import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../../global/components";
import CustomInput from "../../../../global/components/CustomInput/CustomInput";
import CustomSelect from "../../../../global/components/CustomSelect/CustomSelect";
import strings from "../../../../global/constants/StringConstants";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import { selectUserEmail } from "../../../../redux/authSlice";
import { useAppSelector } from "../../../../utils/hooks";
import { theme } from "../../../../utils/styles";
import {
  getPreference,
  updatePreference,
} from "../../../VedazTalk/VedazTalkService";
import contactUsDialogStyles from "../ContactUsDialog/ContactUsDialog.styles";
import preferenceStyles from "./Preference.styles";

declare global {
  interface Window {
    google: {
      maps: {
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            options?: any
          ) => google.maps.places.Autocomplete;
        };
        event: {
          addListener: (
            instance: any,
            eventName: string,
            handler: Function
          ) => void;
        };
      };
    };
  }
}

interface CustomProps {
  open: boolean;
  setOpen: Function;
  drawerWidth?: any;
  setIsLoading: Function;
  setIsPreferenceLoading: Function;
  handlePreferenceChange: Function;
}

interface Address {
  latitude: string;
  longitude: string;
  location: string;
}

const PreferencesDialog = (props: CustomProps) => {
  const classes = contactUsDialogStyles;
  const preferencesClasses = preferenceStyles;
  const { t } = useTranslation();
  const loginUser = useAppSelector(selectUserEmail);
  const [disabled, setDisabled] = useState(false);
  const [initialPreferences, setInitialPreferences] = useState<any>(null); // Add this line
  const [preferences, setPreferences] = useState({
    ageFrom: {
      value: "",
      error: "",
    },
    ageTo: {
      value: "",
      error: "",
    },
    cityKmRange: {
      value: "20000",
      error: "",
    },
    birthPlace: {
      value: "",
      error: "",
    },
    sex: {
      value: "0",
      error: "",
    },
  });
  const [address, setAddress] = useState<Address>({
    latitude: "",
    longitude: "",
    location: "",
  });

  const genderMenu = [
    { value: "Male", content: `${t("man")}` },
    { value: "Female", content: `${t("woman")}` },
    { value: "Both", content: `${t("both")}` },
  ];

  useEffect(() => {
    if (props.open) {
      const initTimeout = setTimeout(() => {
        initialize();
      }, 300);
      return () => clearTimeout(initTimeout);
    }
  }, [props.open]);

  useEffect(() => {
    if (address.location) {
      setPreferences({
        ...preferences,
        birthPlace: {
          ...preferences.birthPlace,
          value: address.location,
        },
      });
    } else if (!preferences.birthPlace.value) {
      setAddress({
        ...address,
        location: "",
      });
    }
  }, [address.location]);

  useEffect(() => {
    const visitedUsers = JSON.parse(
      localStorage.getItem("visitedUsers") || "[]"
    );
    if (!visitedUsers.includes(loginUser)) {
      getPreferenceData();
    }
  }, []);

  useEffect(() => {
    if (props.open) {
      const visitedUsers = JSON.parse(
        localStorage.getItem("visitedUsers") || "[]"
      );
      if (visitedUsers.includes(loginUser)) {
        getPreferenceData();
      }
    }
  }, [props.open]);

  const handlePlaceChanged = (autocomplete: any) => {
    try {
      const place = autocomplete.getPlace();
      if (place && place.geometry && place.geometry.location) {
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        setAddress({
          latitude: latitude.toString(),
          longitude: longitude.toString(),
          location: place.formatted_address || "",
        });
      }
    } catch (error) {
      console.error("Error handling place change:", error);
    }
  };

  function initialize() {
    try {
      const input = document.getElementById("birthPlace") as HTMLInputElement;
      if (
        input &&
        window.google &&
        window.google.maps &&
        window.google.maps.places
      ) {
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ["(cities)"],
        });

        google.maps.event.addListener(autocomplete, "place_changed", () =>
          handlePlaceChanged(autocomplete)
        );

        setTimeout(() => {
          const pacContainer = document.querySelector(".pac-container");
          if (pacContainer) {
            (pacContainer as HTMLElement).style.zIndex = "1500";
          }
        }, 100);
      } else {
        console.error("Google Maps API not loaded correctly");
      }
    } catch (error) {
      console.error("Error initializing autocomplete:", error);
    }
  }

  const getPreferenceData = async () => {
    try {
      props.setIsPreferenceLoading(true);
      const visitedUsers = JSON.parse(
        localStorage.getItem("visitedUsers") || "[]"
      );

      const response = await getPreference(loginUser);
      const newPreferences = {
        ageFrom: {
          value: response.ageFrom,
          error: "",
        },
        ageTo: {
          value: response.ageTo,
          error: "",
        },
        cityKmRange: {
          value: response.cityKmRange,
          error: "",
        },
        birthPlace: {
          value: response.city,
          error: "",
        },
        sex: {
          value: response.sex,
          error: "",
        },
      };

      setPreferences(newPreferences);
      setInitialPreferences(newPreferences); // Store initial values
      props.setIsPreferenceLoading(false);

      if (response.city && response.latitude && response.longitude) {
        setAddress({
          latitude: response.latitude.toString(),
          longitude: response.longitude.toString(),
          location: response.city,
        });
      }
      if (!visitedUsers.includes(loginUser)) {
        props.setOpen(true);
        visitedUsers.push(loginUser);
        localStorage.setItem("visitedUsers", JSON.stringify(visitedUsers));
      } else {
        props.setIsPreferenceLoading(false);
      }
    } catch (error: any) {
      props.setIsPreferenceLoading(false);
      handleErrorMessage(error);
    }
  };

  const validateFields = (): boolean => {
    let isValid = true;
    const newPreferences = { ...preferences };

    // if (!preferences.birthPlace.value) {
    //   newPreferences.birthPlace.error = t("cityRequired");
    //   isValid = false;
    // }

    // if (!preferences.cityKmRange.value) {
    //   newPreferences.cityKmRange.error = t("rangeRequired");
    //   isValid = false;
    // }

    if (!preferences.sex.value) {
      newPreferences.sex.error = t("sexRequired");
      isValid = false;
    }

    if (!preferences.ageFrom.value) {
      newPreferences.ageFrom.error = t("ageFromRequired");
      isValid = false;
    }

    if (!preferences.ageTo.value) {
      newPreferences.ageTo.error = t("ageToRequired");
      isValid = false;
    }

    if (preferences.ageFrom.value > preferences.ageTo.value) {
      openErrorNotification("Age from must be less than age to.");
      isValid = false;
    }

    setPreferences(newPreferences);
    return isValid;
  };

  const updatePreferenceData = async () => {
    try {
      props.setIsLoading(true);
      if (!validateFields()) {
        return;
      }
      setDisabled(true);
      const obj = {
        email: loginUser,
        newPrefs: {
          ageFrom: +preferences.ageFrom.value,
          ageTo: +preferences.ageTo.value,
          cityKmRange: +preferences.cityKmRange.value || 20000,
          city: preferences.birthPlace.value,
          sex: preferences.sex.value,
          latitude: address.latitude ? +address.latitude : 0,
          longitude: address.longitude ? +address.longitude : 0,
        },
      };
      const response = await updatePreference(obj);
      openSuccessNotification(response.message);
      props.setOpen(false);
      props.handlePreferenceChange();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      props.setIsLoading(false);
      setDisabled(false);
    }
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    let newValue = value;

    switch (name) {
      case "cityKmRange":
        const kmValue = parseInt(value);
        if (!isNaN(kmValue)) {
          newValue = Math.min(Math.max(kmValue, 0), 40000).toString();
        }
        break;

      case "ageFrom":
        const ageFromValue = parseInt(value);
        if (!isNaN(ageFromValue)) {
          let validAge = Math.min(Math.max(ageFromValue, 1), 150);
          newValue = validAge.toString();
        }
        break;

      case "ageTo":
        const ageToValue = parseInt(value);
        if (!isNaN(ageToValue)) {
          let validAge = Math.min(Math.max(ageToValue, 1), 150);
          newValue = validAge.toString();
        }
        break;
    }

    setPreferences({
      ...preferences,
      [name]: {
        ...preferences[name as keyof typeof preferences],
        value: newValue,
        error: "",
      },
    });

    if (name === "birthPlace" && value === "") {
      setAddress({
        latitude: "",
        longitude: "",
        location: "",
      });
    }
  };

  // Add this function to check for changes
  const hasPreferencesChanged = () => {
    if (!initialPreferences) return false;

    return (
      preferences.ageFrom.value !== initialPreferences.ageFrom.value ||
      preferences.ageTo.value !== initialPreferences.ageTo.value ||
      preferences.cityKmRange.value !== initialPreferences.cityKmRange.value ||
      preferences.birthPlace.value !== initialPreferences.birthPlace.value ||
      preferences.sex.value !== initialPreferences.sex.value
    );
  };

  const dialogBodyContent = () => {
    return (
      <Stack direction={"column"} pb={5} spacing={2} px={2}>
        <CustomInput
          label={t("city")}
          placeHolder={t("cityPlace")}
          id="birthPlace"
          type="text"
          name="birthPlace"
          customClasses={classes.textField}
          value={preferences.birthPlace.value}
          onChange={handleFieldChange}
          error={
            !isTruthy(
              preferences.birthPlace.value &&
                address.latitude &&
                address.longitude
            ) && preferences.birthPlace.error
          }
        />
        <CustomInput
          label={t("range")}
          name="cityKmRange"
          id="cityKmRange"
          placeHolder={t("rangePlace")}
          customClasses={classes.textField}
          value={preferences.cityKmRange.value}
          onChange={handleFieldChange}
          error={
            !isTruthy(preferences.cityKmRange.value) &&
            preferences.cityKmRange.error
          }
        />
        <CustomSelect
          required
          menuItems={genderMenu}
          label={t("sex")}
          id={"sex"}
          name={"sex"}
          customClasses={classes.textField}
          onChange={handleFieldChange}
          value={preferences.sex.value}
          error={preferences.sex.error}
        />
        <Stack direction={"row"} spacing={2} width={"100%"}>
          <Box width={"100%"}>
            <CustomInput
              required
              label={t("ageFrom")}
              name="ageFrom"
              id="ageFrom"
              placeHolder={t("ageFrom")}
              value={preferences.ageFrom.value}
              customClasses={classes.textField}
              onChange={handleFieldChange}
              error={
                !isTruthy(preferences.ageFrom.value) &&
                preferences.ageFrom.error
              }
            />
          </Box>
          <Box width={"100%"}>
            <CustomInput
              required
              label={t("ageTo")}
              name="ageTo"
              id="ageTo"
              placeHolder={t("ageTo")}
              value={preferences.ageTo.value}
              customClasses={classes.textField}
              onChange={handleFieldChange}
              error={
                !isTruthy(preferences.ageTo.value) && preferences.ageTo.error
              }
            />
          </Box>
        </Stack>
        <Stack direction={"row"} justifyContent={"center"}>
          <CustomButton
            label={t("update")}
            disabled={disabled || !hasPreferencesChanged()} // Modify this line
            type={strings.PRIMARY}
            onClick={updatePreferenceData}
            customClasses={{ maxWidth: "250px", mt: "32px !important" }}
          />
        </Stack>
      </Stack>
    );
  };

  const handleDrawerClose = () => {
    props.setOpen(false);
  };

  const getPreferencesDrawer = () => {
    const drawerWidth = props.drawerWidth ? props.drawerWidth : 600;

    return (
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [theme.breakpoints.down("sm")]: {
            width: "100%",
          },
          "& .MuiDrawer-paper": {
            background: "#05022A",
            width: drawerWidth,
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: "10px",
              border: "1px solid #FFFFFF",
              background: "#282945",
            },
          },
        }}
        variant="persistent"
        anchor="right"
        open={props.open}
      >
        <Stack
          direction={"row"}
          sx={classes.headerBox}
          pt={6}
          px={{ sm: 5, xs: 2.5 }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction={"row"} spacing={2}>
            <Typography sx={preferencesClasses.headerText}>
              {t("preferenceHeader")}
            </Typography>
          </Stack>
          <IconButton
            onClick={handleDrawerClose}
            sx={{
              border: "1px solid white",
              borderRadius: "50px",
              padding: "2px",
            }}
          >
            <CloseIcon fontSize="small" htmlColor="#ffffff" />
          </IconButton>
        </Stack>
        {dialogBodyContent()}
      </Drawer>
    );
  };

  return getPreferencesDrawer();
};

export default PreferencesDialog;
